import { type FC, type ReactNode } from 'react'

import { FormControl, FormHelperText, FormLabel, Option, Select } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormModel } from '../../marketplacePartnerRegisterPageMakeFormState'
import { type makeMarketplacePartnerRegisterPageFormValidations } from '../../marketplacePartnerRegisterPageMakeFormValidations'
import { useMarketplacePartnerRegisterPageContext } from '../marketplacePartnerRegisterPageContext'

type MarketplacePartnerRegisterPageSelectFieldName = Extract<
  keyof MarketplacePartnerRegisterPageFormModel,
  keyof ReturnType<typeof makeMarketplacePartnerRegisterPageFormValidations>
>

type MarketplacePartnerRegisterPageFormSelectFieldProps = {
  disabled?: boolean
  fieldName: MarketplacePartnerRegisterPageSelectFieldName
  label?: ReactNode
  optionEntries?: {
    label: string
    value: string
  }[]
  options?: string[]
  placeholder?: ReactNode
  required?: boolean
}
export const MarketplacePartnerRegisterPageFormSelectField: FC<
  MarketplacePartnerRegisterPageFormSelectFieldProps
> = ({
  disabled = false,
  fieldName,
  label,
  optionEntries,
  options,
  placeholder,
  required = false,
}) => {
  const { formState } = useMarketplacePartnerRegisterPageContext()
  const { model } = formState
  formState.use()

  const fieldId = `marketplace-partner-register-page-form-select-field-select-${fieldName}`
  const selectValue = model[fieldName] as string
  const resolvedOptionEntries =
    optionEntries ??
    options?.map((option) => ({ label: option, value: option }))

  return (
    <div className="flex flex-col gap-4">
      <FormControl
        data-qa-id={`sellerForm-formControl-${fieldName}`}
        disabled={disabled}
        error={!model.validator.isPropertyValid(fieldName)}
        id={`marketplace-partner-register-page-form-select-field-form-control-${fieldName}`}
        value={selectValue}
      >
        <FormLabel htmlFor={fieldId}>
          {label}
          {required ? '*' : ''}
        </FormLabel>
        <Select
          data-qa-id={`sellerForm-select-${fieldName}`}
          onChange={(_event, value): void => {
            if (!value) {
              return
            }
            formState.setValue(value, model, fieldName)
            model.validator.validate(fieldName)
          }}
          placeholder={disabled ? undefined : placeholder}
          slotProps={{
            root: {
              'aria-labelledby': fieldId,
            },
          }}
          title="marketplace-partner-register-page-form-select-field"
        >
          {resolvedOptionEntries?.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        {model.validator.getFirstErrorFor(fieldName) && (
          <FormHelperText
            data-qa-id={`global-MessageForm-selectInput-error-msg-${fieldName}`}
            error
          >
            {model.validator.getFirstErrorFor(fieldName)}
          </FormHelperText>
        )}
      </FormControl>
    </div>
  )
}
